import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useI18n } from '../contexts/I18nContext';
import { getErrorMessage } from '@/lib/utils';
import axios from 'axios';
import * as XLSX from 'xlsx';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Progress } from '../components/ui/progress';
import { 
  Upload,
  FileSpreadsheet,
  FileText,
  ArrowLeft,
  ArrowRight,
  Check,
  AlertCircle,
  Sparkles,
  Package,
  Loader2,
  FolderOpen,
  Plus
} from 'lucide-react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ImportCatalogPage = () => {
  const { t } = useI18n();
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  
  const [step, setStep] = useState(1); // 1: upload, 2: mapping, 3: preview, 4: complete
  const [importMethod, setImportMethod] = useState('csv');
  const [file, setFile] = useState(null);
  const [textInput, setTextInput] = useState('');
  const [parsedData, setParsedData] = useState([]);
  const [columnMapping, setColumnMapping] = useState({
    name: '',
    sku: '',
    category: '',
    unit: '',
    description: '',
    price: ''
  });
  const [headers, setHeaders] = useState([]);
  const [importing, setImporting] = useState(false);
  const [progress, setProgress] = useState(0);
  const [importResults, setImportResults] = useState(null);
  
  // Stati per la selezione del catalogo
  const [catalogs, setCatalogs] = useState([]);
  const [selectedCatalog, setSelectedCatalog] = useState('none'); // 'none', 'new', o ID catalogo
  const [newCatalogName, setNewCatalogName] = useState('');
  const [loadingCatalogs, setLoadingCatalogs] = useState(true);

  // Carica i cataloghi esistenti all'avvio
  useEffect(() => {
    fetchCatalogs();
  }, []);

  const fetchCatalogs = async () => {
    try {
      const res = await axios.get(`${API}/catalogs`);
      setCatalogs(res.data);
    } catch (error) {
      console.error('Error fetching catalogs:', error);
    } finally {
      setLoadingCatalogs(false);
    }
  };

  // Gestisce il cambio file selezionato
  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      setFile(selectedFile);
      parseFile(selectedFile);
    }
  };

  // Determina il tipo di file e chiama il parser appropriato
  const parseFile = (file) => {
    const fileName = file.name.toLowerCase();
    const isExcel = fileName.endsWith('.xlsx') || fileName.endsWith('.xls');
    
    if (isExcel) {
      // Parsing file Excel con libreria XLSX
      parseExcelFile(file);
    } else {
      // Parsing file CSV come testo
      const reader = new FileReader();
      reader.onload = (e) => {
        const text = e.target.result;
        parseCSV(text);
      };
      reader.readAsText(file);
    }
  };

  // Parser per file Excel (.xlsx, .xls)
  const parseExcelFile = (file: File) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const data = new Uint8Array(e.target?.result as ArrayBuffer);
        const workbook = XLSX.read(data, { type: 'array' });
        
        // Prende il primo foglio
        const firstSheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[firstSheetName];
        
        // Converte in array di array
        const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1, defval: '' }) as any[][];
        
        if (jsonData.length < 2) {
          toast.error('Il file deve contenere almeno un\'intestazione e una riga di dati');
          return;
        }
        
        // Estrae intestazioni e filtra quelle vuote, mantenendo l'indice originale
        const rawHeaders = jsonData[0] as any[];
        const validHeadersWithIndex = rawHeaders
          .map((h, index) => ({ header: String(h || '').trim(), originalIndex: index }))
          .filter(item => item.header !== '');
        
        // Array di intestazioni valide per la visualizzazione
        const parsedHeaders = validHeadersWithIndex.map(item => item.header);
        
        // Mappa: indice visuale -> indice originale
        const indexMap = validHeadersWithIndex.map(item => item.originalIndex);
        
        setHeaders(parsedHeaders);
        
        // Auto-detect column mapping usando indici visuali
        const autoMapping = { name: '', sku: '', category: '', unit: '', description: '', price: '' };
        parsedHeaders.forEach((header, visualIndex) => {
          const h = header.toLowerCase();
          if (h.includes('nome') || h.includes('name') || h.includes('prodotto') || h.includes('product') || h.includes('vino') || h.includes('wine')) {
            autoMapping.name = visualIndex.toString();
          } else if (h.includes('sku') || h.includes('codice') || h.includes('code')) {
            autoMapping.sku = visualIndex.toString();
          } else if (h.includes('categ') || h.includes('tipo') || h.includes('type') || h.includes('regione') || h.includes('region')) {
            autoMapping.category = visualIndex.toString();
          } else if (h.includes('unit') || h.includes('misura') || h.includes('um') || h.includes('formato') || h.includes('format')) {
            autoMapping.unit = visualIndex.toString();
          } else if (h.includes('desc') || h.includes('note') || h.includes('annata') || h.includes('year')) {
            autoMapping.description = visualIndex.toString();
          } else if (h.includes('prezz') || h.includes('price') || h.includes('costo') || h.includes('cost') || h.includes('€') || h.includes('euro') || h.includes('eur')) {
            autoMapping.price = visualIndex.toString();
          }
        });
        setColumnMapping(autoMapping);
        
        // Parse righe dati, estraendo solo le colonne valide
        const dataRows = jsonData.slice(1)
          .filter(row => row.some(cell => cell !== '' && cell !== null && cell !== undefined))
          .map(row => indexMap.map(origIndex => String(row[origIndex] || '').trim()));
        
        setParsedData(dataRows);
        setStep(2);
        
        toast.success(`File Excel caricato: ${parsedHeaders.length} colonne, ${dataRows.length} righe`);
      } catch (error) {
        console.error('Errore parsing Excel:', error);
        toast.error('Errore nel parsing del file Excel. Verifica che il file sia valido.');
      }
    };
    reader.readAsArrayBuffer(file);
  };

  // Parser per file CSV
  const parseCSV = (text) => {
    const lines = text.split('\n').filter(line => line.trim());
    if (lines.length < 2) {
      toast.error('Il file deve contenere almeno un\'intestazione e una riga di dati');
      return;
    }

    // Parse header
    const headerLine = lines[0];
    const delimiter = headerLine.includes(';') ? ';' : ',';
    const rawHeaders = headerLine.split(delimiter).map(h => h.trim().replace(/^"|"$/g, ''));
    
    // Filtra intestazioni vuote mantenendo traccia degli indici originali
    const validHeadersWithIndex = rawHeaders
      .map((h, index) => ({ header: h, originalIndex: index }))
      .filter(item => item.header !== '');
    
    const parsedHeaders = validHeadersWithIndex.map(item => item.header);
    const indexMap = validHeadersWithIndex.map(item => item.originalIndex);
    
    setHeaders(parsedHeaders);

    // Auto-detect column mapping
    const autoMapping = { name: '', sku: '', category: '', unit: '', description: '', price: '' };
    parsedHeaders.forEach((header, visualIndex) => {
      const h = header.toLowerCase();
      if (h.includes('nome') || h.includes('name') || h.includes('prodotto') || h.includes('product')) {
        autoMapping.name = visualIndex.toString();
      } else if (h.includes('sku') || h.includes('codice') || h.includes('code')) {
        autoMapping.sku = visualIndex.toString();
      } else if (h.includes('categ') || h.includes('tipo') || h.includes('type')) {
        autoMapping.category = visualIndex.toString();
      } else if (h.includes('unit') || h.includes('misura') || h.includes('um')) {
        autoMapping.unit = visualIndex.toString();
      } else if (h.includes('desc') || h.includes('note')) {
        autoMapping.description = visualIndex.toString();
      } else if (h.includes('prezz') || h.includes('price') || h.includes('costo') || h.includes('cost') || h.includes('€') || h.includes('euro')) {
        autoMapping.price = visualIndex.toString();
      }
    });
    setColumnMapping(autoMapping);

    // Parse data rows, estraendo solo le colonne valide
    const dataRows = lines.slice(1).map(line => {
      const allValues = line.split(delimiter).map(v => v.trim().replace(/^"|"$/g, ''));
      return indexMap.map(origIndex => allValues[origIndex] || '');
    });
    setParsedData(dataRows);

    setStep(2);
  };

  const parseTextInput = () => {
    if (!textInput.trim()) {
      toast.error('Inserisci una lista di prodotti');
      return;
    }

    const lines = textInput.split('\n').filter(line => line.trim());
    const products = lines.map(line => {
      // Try to parse "name - price" or "name - description - price" or just "name"
      const parts = line.split(' - ').map(p => p.trim());
      
      let name = parts[0] || '';
      let description = '';
      let priceStr = '';
      
      if (parts.length >= 2) {
        const lastPart = parts[parts.length - 1];
        // Check if last part looks like a price (contains numbers and optional comma/dot)
        if (/^[\d.,€\s]+$/.test(lastPart.replace(/EUR/gi, ''))) {
          priceStr = lastPart;
          description = parts.length > 2 ? parts.slice(1, -1).join(' - ') : '';
        } else {
          description = parts.slice(1).join(' - ');
        }
      }
      
      return [name, '', '', '', description, priceStr];
    });

    setHeaders(['Nome', 'SKU', 'Categoria', 'Unità', 'Descrizione', 'Prezzo']);
    setColumnMapping({ name: '0', sku: '1', category: '2', unit: '3', description: '4', price: '5' });
    setParsedData(products);
    setStep(2);
  };

  // Converte prezzo dal formato europeo (virgola) al formato numerico
  // Gestisce: "12,50" -> 12.50, "1.250,00" -> 1250.00, "€ 12,50" -> 12.50
  const parseEuroPrice = (value) => {
    if (!value) return null;
    
    // Rimuovi simbolo euro, spazi e caratteri non numerici (tranne virgole e punti)
    let cleaned = String(value)
      .replace(/€/g, '')
      .replace(/EUR/gi, '')
      .replace(/\s/g, '')
      .trim();
    
    if (!cleaned) return null;
    
    // Conta virgole e punti
    const commaCount = (cleaned.match(/,/g) || []).length;
    const dotCount = (cleaned.match(/\./g) || []).length;
    
    // Caso: formato europeo con virgola decimale (es. "12,50" o "1.250,50")
    if (commaCount === 1 && cleaned.indexOf(',') > cleaned.lastIndexOf('.')) {
      // La virgola è il separatore decimale
      cleaned = cleaned.replace(/\./g, '').replace(',', '.');
    }
    // Caso: formato europeo solo virgola (es. "12,50")
    else if (commaCount === 1 && dotCount === 0) {
      cleaned = cleaned.replace(',', '.');
    }
    // Caso: formato americano con punto decimale (es. "12.50")
    else if (dotCount === 1 && commaCount === 0) {
      // Già nel formato corretto
    }
    // Caso: migliaia con punto, nessun decimale (es. "1.250")
    else if (dotCount >= 1 && commaCount === 0) {
      // Assumiamo che i punti siano separatori delle migliaia
      cleaned = cleaned.replace(/\./g, '');
    }
    
    const parsed = parseFloat(cleaned);
    return isNaN(parsed) || parsed < 0 ? null : parsed;
  };

  const getMappedProducts = () => {
    return parsedData.map(row => {
      const priceValue = columnMapping.price ? row[parseInt(columnMapping.price)] : null;
      const parsedPrice = parseEuroPrice(priceValue);
      
      return {
        name: columnMapping.name ? row[parseInt(columnMapping.name)] || '' : '',
        sku: columnMapping.sku ? row[parseInt(columnMapping.sku)] || '' : '',
        category: columnMapping.category ? row[parseInt(columnMapping.category)] || '' : '',
        unit_of_measure: columnMapping.unit ? row[parseInt(columnMapping.unit)] || 'pz' : 'pz',
        description: columnMapping.description ? row[parseInt(columnMapping.description)] || '' : '',
        _price: parsedPrice // Campo temporaneo per il prezzo
      };
    }).filter(p => p.name); // Filter out empty names
  };

  const handleImport = async () => {
    const products = getMappedProducts();
    if (products.length === 0) {
      toast.error('Nessun prodotto valido da importare');
      return;
    }

    // Validazione catalogo nuovo
    if (selectedCatalog === 'new' && !newCatalogName.trim()) {
      toast.error('Inserisci un nome per il nuovo catalogo');
      return;
    }

    setImporting(true);
    setProgress(0);
    
    const results = { success: 0, failed: 0, errors: [], productIds: [] };
    
    // Se si deve creare un nuovo catalogo, fallo prima
    let targetCatalogId = selectedCatalog !== 'none' && selectedCatalog !== 'new' ? selectedCatalog : null;
    
    if (selectedCatalog === 'new') {
      try {
        const catalogRes = await axios.post(`${API}/catalogs`, {
          name: newCatalogName.trim(),
          description: `Importato il ${new Date().toLocaleDateString('it-IT')}`,
          is_visible: true,
          product_ids: []
        });
        targetCatalogId = catalogRes.data.id;
        toast.success(`Catalogo "${newCatalogName}" creato`);
      } catch (error) {
        toast.error('Errore nella creazione del catalogo');
        setImporting(false);
        return;
      }
    }
    
    // Importa i prodotti
    const productsWithPrices = []; // Per salvare i prezzi dopo
    for (let i = 0; i < products.length; i++) {
      try {
        // Estrai il prezzo prima di inviare (non va al backend come campo prodotto)
        const { _price, ...productData } = products[i];
        const res = await axios.post(`${API}/products`, productData);
        results.success++;
        results.productIds.push(res.data.id);
        
        // Salva per aggiornare il prezzo dopo
        if (_price && _price > 0) {
          productsWithPrices.push({ id: res.data.id, price: _price });
        }
      } catch (error: any) {
        results.failed++;
        results.errors.push(`${products[i].name}: ${getErrorMessage(error, 'Errore')}`);
      }
      setProgress(Math.round(((i + 1) / products.length) * 70)); // 70% per import prodotti
    }

    // Salva i prezzi nel listino standard
    if (productsWithPrices.length > 0) {
      let pricesSaved = 0;
      for (const item of productsWithPrices) {
        try {
          await axios.put(`${API}/pricelists/standard/item/${item.id}`, { price: item.price });
          pricesSaved++;
        } catch (error) {
          console.error(`Error saving price for ${item.id}:`, error);
        }
      }
      setProgress(85);
      if (pricesSaved > 0) {
        toast.success(`${pricesSaved} prezzi salvati nel listino standard`);
      }
    }

    // Se c'è un catalogo target, aggiungi i prodotti importati
    if (targetCatalogId && results.productIds.length > 0) {
      let catalogAdded = 0;
      let catalogFailed = 0;
      
      for (const productId of results.productIds) {
        try {
          await axios.post(`${API}/catalogs/${targetCatalogId}/products/${productId}`);
          catalogAdded++;
        } catch (error) {
          console.error(`Error adding product ${productId} to catalog:`, error);
          catalogFailed++;
        }
      }
      
      setProgress(95);
      
      if (catalogAdded > 0) {
        toast.success(`${catalogAdded} prodotti aggiunti al catalogo`);
      }
      if (catalogFailed > 0) {
        toast.error(`${catalogFailed} prodotti non aggiunti al catalogo`);
      }
    }

    setImportResults(results);
    setStep(4);
    setImporting(false);

    if (results.success > 0) {
      toast.success(`${results.success} prodotti importati con successo`);
    }
  };

  const renderStep1 = () => (
    <div className="space-y-6">
      {/* Import Method Selection */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <button
          onClick={() => setImportMethod('csv')}
          className={`p-6 rounded-lg border-2 text-left transition-colors ${
            importMethod === 'csv' ? 'border-primary bg-primary/5' : 'border-border hover:border-primary/50'
          }`}
        >
          <FileSpreadsheet className={`h-8 w-8 mb-3 ${importMethod === 'csv' ? 'text-primary' : 'text-muted-foreground'}`} />
          <h3 className="font-heading font-semibold">File CSV/Excel</h3>
          <p className="text-sm text-muted-foreground mt-1">
            Carica un file .csv o .xlsx con i tuoi prodotti
          </p>
        </button>
        <button
          onClick={() => setImportMethod('text')}
          className={`p-6 rounded-lg border-2 text-left transition-colors ${
            importMethod === 'text' ? 'border-primary bg-primary/5' : 'border-border hover:border-primary/50'
          }`}
        >
          <FileText className={`h-8 w-8 mb-3 ${importMethod === 'text' ? 'text-primary' : 'text-muted-foreground'}`} />
          <h3 className="font-heading font-semibold">Lista Testuale</h3>
          <p className="text-sm text-muted-foreground mt-1">
            Incolla una lista di prodotti (uno per riga)
          </p>
        </button>
      </div>

      {/* Upload Area */}
      {importMethod === 'csv' ? (
        <div 
          onClick={() => fileInputRef.current?.click()}
          className="border-2 border-dashed border-border rounded-lg p-12 text-center cursor-pointer hover:border-primary/50 transition-colors"
        >
          <input
            ref={fileInputRef}
            type="file"
            accept=".csv,.xlsx,.xls"
            onChange={handleFileChange}
            className="hidden"
            data-testid="file-input"
          />
          <Upload className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
          <p className="font-medium">Clicca o trascina un file qui</p>
          <p className="text-sm text-muted-foreground mt-1">
            Supporta CSV, XLSX (max 1000 righe)
          </p>
          {file && (
            <p className="mt-4 text-primary font-medium">
              File selezionato: {file.name}
            </p>
          )}
        </div>
      ) : (
        <div className="space-y-3">
          <Label>Lista Prodotti</Label>
          <Textarea
            value={textInput}
            onChange={(e) => setTextInput(e.target.value)}
            placeholder={`Pomodori San Marzano DOP
Mozzarella di Bufala - Fresca, 125g
Olio Extra Vergine di Oliva
Pasta di Gragnano - Spaghetti
...`}
            rows={10}
            className="font-mono text-sm"
            data-testid="text-input"
          />
          <p className="text-xs text-muted-foreground">
            Un prodotto per riga. Usa " - " per separare nome e descrizione.
          </p>
          <Button onClick={parseTextInput} disabled={!textInput.trim()}>
            Analizza Lista
            <ArrowRight className="ml-2 h-4 w-4" />
          </Button>
        </div>
      )}
    </div>
  );

  const renderStep2 = () => (
    <div className="space-y-6">
      <div className="bg-muted/50 rounded-lg p-4">
        <p className="font-medium">Trovate {parsedData.length} righe</p>
        <p className="text-sm text-muted-foreground">Associa le colonne ai campi del prodotto</p>
      </div>

      {/* Column Mapping */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        <div className="space-y-2">
          <Label>Nome Prodotto *</Label>
          <Select value={columnMapping.name} onValueChange={(v) => setColumnMapping({...columnMapping, name: v})}>
            <SelectTrigger data-testid="mapping-name">
              <SelectValue placeholder="Seleziona colonna" />
            </SelectTrigger>
            <SelectContent>
              {headers.map((h, i) => (
                <SelectItem key={i} value={i.toString()}>{h}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-2">
          <Label>SKU / Codice</Label>
          <Select value={columnMapping.sku || 'none'} onValueChange={(v) => setColumnMapping({...columnMapping, sku: v === 'none' ? '' : v})}>
            <SelectTrigger>
              <SelectValue placeholder="Seleziona colonna" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="none">-- Nessuno --</SelectItem>
              {headers.map((h, i) => (
                <SelectItem key={i} value={i.toString()}>{h}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-2">
          <Label>Categoria</Label>
          <Select value={columnMapping.category || 'none'} onValueChange={(v) => setColumnMapping({...columnMapping, category: v === 'none' ? '' : v})}>
            <SelectTrigger>
              <SelectValue placeholder="Seleziona colonna" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="none">-- Nessuno --</SelectItem>
              {headers.map((h, i) => (
                <SelectItem key={i} value={i.toString()}>{h}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-2">
          <Label>Unità di Misura</Label>
          <Select value={columnMapping.unit || 'none'} onValueChange={(v) => setColumnMapping({...columnMapping, unit: v === 'none' ? '' : v})}>
            <SelectTrigger>
              <SelectValue placeholder="Seleziona colonna" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="none">-- Nessuno --</SelectItem>
              {headers.map((h, i) => (
                <SelectItem key={i} value={i.toString()}>{h}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-2">
          <Label>Descrizione</Label>
          <Select value={columnMapping.description || 'none'} onValueChange={(v) => setColumnMapping({...columnMapping, description: v === 'none' ? '' : v})}>
            <SelectTrigger>
              <SelectValue placeholder="Seleziona colonna" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="none">-- Nessuno --</SelectItem>
              {headers.map((h, i) => (
                <SelectItem key={i} value={i.toString()}>{h}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-2">
          <Label>Prezzo (€)</Label>
          <Select value={columnMapping.price || 'none'} onValueChange={(v) => setColumnMapping({...columnMapping, price: v === 'none' ? '' : v})}>
            <SelectTrigger>
              <SelectValue placeholder="Seleziona colonna" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="none">-- Nessuno --</SelectItem>
              {headers.map((h, i) => (
                <SelectItem key={i} value={i.toString()}>{h}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <p className="text-xs text-muted-foreground">
            Formato europeo accettato (es. 12,50 o 1.250,00)
          </p>
        </div>
      </div>

      <div className="flex gap-2">
        <Button variant="outline" onClick={() => setStep(1)}>
          <ArrowLeft className="mr-2 h-4 w-4" />
          Indietro
        </Button>
        <Button onClick={() => setStep(3)} disabled={!columnMapping.name}>
          Anteprima
          <ArrowRight className="ml-2 h-4 w-4" />
        </Button>
      </div>
    </div>
  );

  const renderStep3 = () => {
    const mappedProducts = getMappedProducts();
    
    return (
      <div className="space-y-6">
        <div className="bg-muted/50 rounded-lg p-4 flex items-center justify-between">
          <div>
            <p className="font-medium">{mappedProducts.length} prodotti pronti per l'import</p>
            <p className="text-sm text-muted-foreground">Verifica l'anteprima prima di confermare</p>
          </div>
          <Package className="h-8 w-8 text-primary" />
        </div>

        {/* Selezione Catalogo */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <FolderOpen className="h-5 w-5" />
              Organizza in un sotto-catalogo
            </CardTitle>
            <CardDescription>
              I sotto-cataloghi ti permettono di organizzare i prodotti per categoria (es. "Ortofrutta", "Latticini", "Bevande"). 
              I tuoi clienti vedranno i prodotti organizzati per catalogo quando consultano il tuo listino.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="bg-blue-50 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-800 rounded-lg p-3 text-sm">
              <p className="font-medium text-blue-800 dark:text-blue-300 mb-1">💡 Come funzionano i sotto-cataloghi?</p>
              <ul className="text-blue-700 dark:text-blue-400 space-y-1 ml-4 list-disc">
                <li>I prodotti importati saranno sempre visibili nel <strong>catalogo generale</strong></li>
                <li>Assegnandoli a un sotto-catalogo, potrai filtrarli e organizzarli meglio</li>
                <li>Puoi anche saltare questo passaggio e organizzarli dopo</li>
              </ul>
            </div>
            
            <Select 
              value={selectedCatalog} 
              onValueChange={setSelectedCatalog}
              disabled={loadingCatalogs}
            >
              <SelectTrigger data-testid="catalog-select">
                <SelectValue placeholder="Seleziona sotto-catalogo..." />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">-- Solo catalogo generale --</SelectItem>
                <SelectItem value="new">
                  <span className="flex items-center gap-2">
                    <Plus className="h-4 w-4" />
                    Crea nuovo sotto-catalogo...
                  </span>
                </SelectItem>
                {catalogs.map((catalog) => (
                  <SelectItem key={catalog.id} value={catalog.id}>
                    {catalog.name} ({catalog.product_ids?.length || 0} prodotti)
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            {/* Input per nuovo catalogo */}
            {selectedCatalog === 'new' && (
              <div className="space-y-2 p-3 border rounded-lg bg-muted/30">
                <Label htmlFor="new-catalog-name">Nome del nuovo sotto-catalogo *</Label>
                <Input
                  id="new-catalog-name"
                  value={newCatalogName}
                  onChange={(e) => setNewCatalogName(e.target.value)}
                  placeholder="es. Ortofrutta, Latticini, Bevande..."
                  data-testid="new-catalog-name-input"
                />
              </div>
            )}

            {selectedCatalog !== 'none' && selectedCatalog !== 'new' && (
              <p className="text-sm text-green-600 font-medium">
                ✓ I {mappedProducts.length} prodotti verranno aggiunti al sotto-catalogo "{catalogs.find(c => c.id === selectedCatalog)?.name}"
              </p>
            )}
            
            {selectedCatalog === 'none' && (
              <p className="text-sm text-muted-foreground">
                I prodotti saranno importati nel catalogo generale. Potrai organizzarli in sotto-cataloghi in seguito.
              </p>
            )}
          </CardContent>
        </Card>

        {/* Preview Table */}
        <Card>
          <CardContent className="p-0">
            <div className="max-h-96 overflow-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="uppercase text-xs">#</TableHead>
                    <TableHead className="uppercase text-xs">Nome</TableHead>
                    <TableHead className="uppercase text-xs">SKU</TableHead>
                    <TableHead className="uppercase text-xs">Categoria</TableHead>
                    <TableHead className="uppercase text-xs">Unità</TableHead>
                    <TableHead className="uppercase text-xs text-right">Prezzo</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {mappedProducts.slice(0, 20).map((product, index) => (
                    <TableRow key={index}>
                      <TableCell className="text-muted-foreground">{index + 1}</TableCell>
                      <TableCell className="font-medium">{product.name}</TableCell>
                      <TableCell className="font-mono text-sm">{product.sku || '-'}</TableCell>
                      <TableCell>{product.category || '-'}</TableCell>
                      <TableCell>{product.unit_of_measure}</TableCell>
                      <TableCell className="text-right font-medium">
                        {product._price ? `€ ${product._price.toFixed(2)}` : '-'}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
            {mappedProducts.length > 20 && (
              <div className="p-3 border-t text-center text-sm text-muted-foreground">
                ... e altri {mappedProducts.length - 20} prodotti
              </div>
            )}
          </CardContent>
        </Card>

        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setStep(2)}>
            <ArrowLeft className="mr-2 h-4 w-4" />
            Indietro
          </Button>
          <Button 
            onClick={handleImport} 
            disabled={importing || (selectedCatalog === 'new' && !newCatalogName.trim())} 
            data-testid="start-import-btn"
          >
            {importing ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Importazione...
              </>
            ) : (
              <>
                <Upload className="mr-2 h-4 w-4" />
                Importa {mappedProducts.length} Prodotti
              </>
            )}
          </Button>
        </div>

        {importing && (
          <div className="space-y-2">
            <Progress value={progress} />
            <p className="text-sm text-center text-muted-foreground">{progress}% completato</p>
          </div>
        )}
      </div>
    );
  };

  const renderStep4 = () => (
    <div className="space-y-6">
      <div className="text-center py-8">
        {importResults?.failed === 0 ? (
          <div className="w-16 h-16 rounded-full bg-green-500/10 flex items-center justify-center mx-auto mb-4">
            <Check className="h-8 w-8 text-green-600" />
          </div>
        ) : (
          <div className="w-16 h-16 rounded-full bg-amber-500/10 flex items-center justify-center mx-auto mb-4">
            <AlertCircle className="h-8 w-8 text-amber-600" />
          </div>
        )}
        <h2 className="text-2xl font-heading font-bold">Import Completato</h2>
        <p className="text-muted-foreground mt-2">
          {importResults?.success} prodotti importati con successo
          {importResults?.failed > 0 && `, ${importResults?.failed} errori`}
        </p>
      </div>

      {importResults?.errors?.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base text-destructive">Errori</CardTitle>
          </CardHeader>
          <CardContent>
            <ul className="space-y-1 text-sm">
              {importResults.errors.slice(0, 10).map((err, i) => (
                <li key={i} className="text-muted-foreground">{err}</li>
              ))}
            </ul>
          </CardContent>
        </Card>
      )}

      <div className="flex gap-2 justify-center">
        <Button variant="outline" onClick={() => navigate('/catalog')}>
          Vai al Catalogo
        </Button>
        <Button onClick={() => { setStep(1); setFile(null); setParsedData([]); setTextInput(''); }}>
          Nuovo Import
        </Button>
      </div>
    </div>
  );

  return (
    <div className="space-y-6 max-w-4xl mx-auto" data-testid="import-catalog-page">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" onClick={() => navigate('/catalog')}>
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <div>
          <h1 className="text-2xl font-heading font-bold">Import Catalogo</h1>
          <p className="text-muted-foreground">
            Importa prodotti da file CSV o lista testuale
          </p>
        </div>
      </div>

      {/* Progress Steps */}
      <div className="flex items-center justify-center gap-2">
        {[1, 2, 3, 4].map((s) => (
          <React.Fragment key={s}>
            <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
              step >= s ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground'
            }`}>
              {step > s ? <Check className="h-4 w-4" /> : s}
            </div>
            {s < 4 && <div className={`w-12 h-0.5 ${step > s ? 'bg-primary' : 'bg-muted'}`} />}
          </React.Fragment>
        ))}
      </div>

      {/* Step Content */}
      <Card>
        <CardContent className="pt-6">
          {step === 1 && renderStep1()}
          {step === 2 && renderStep2()}
          {step === 3 && renderStep3()}
          {step === 4 && renderStep4()}
        </CardContent>
      </Card>
    </div>
  );
};

export default ImportCatalogPage;
